import React, { useState, useEffect } from "react";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../api/rootApi";

const MyBooking = () => {
  const [booking, setBooking] = useState([]);
  const [deleteBookingId, setDeleteBookingId] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.email) {
      fetch(`${BASE_URL}/booking/${user.email}`)
        .then((res) => res.json())
        .then((result) => setBooking(result));
    }
  }, [user.email]);

  const handleDelete = (id) => {
    setDeleteBookingId(id);
  };

  const confirmDelete = () => {
    fetch(`${BASE_URL}/booking/${deleteBookingId}`, {
      method: "DELETE",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.deletedCount) {
          const remaining = booking.filter((book) => book._id !== deleteBookingId);
          setBooking(remaining);
          setDeleteBookingId(null);
          alert("Booking deleted successfully!");
        }
      });
  };

  const cancelDelete = () => {
    setDeleteBookingId(null);
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)] py-12">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="mb-10 text-center md:text-left">
          <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-2">My Orders</h2>
          <p className="text-slate-500">Track and manage your pet bookings effortlessly.</p>
        </div>
        
        {booking.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {booking.map((book) => (
              <div
                key={book._id}
                className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 p-6 border border-slate-200/60 flex flex-col"
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold text-slate-900 line-clamp-1">{book.title}</h3>
                  <span
                    className={`px-2.5 py-1 text-xs font-bold rounded-md whitespace-nowrap ${
                      book.status === "Completed" || book.status === "Shipped"
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-amber-50 text-amber-700 border border-amber-200"
                    }`}
                  >
                    {book.status || "Pending"}
                  </span>
                </div>
                
                <div className="space-y-3 mb-6 flex-1 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">👤</span>
                    <span className="font-medium text-slate-700">{book.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">✉️</span>
                    <span>{book.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">📍</span>
                    <span className="line-clamp-1">{book.address}</span>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100 mt-2">
                    <span className="text-slate-400">💰</span>
                    <span className="font-bold text-slate-900">{book.price}</span>
                  </div>
                </div>
                
                <div className="mt-auto pt-4 border-t border-slate-100">
                  <button
                     onClick={() => handleDelete(book._id)}
                    className="w-full py-3 rounded-xl bg-slate-900 text-white font-semibold text-sm hover:bg-amber-500 hover:text-slate-900 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                    Cancel Booking
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200/60 p-16 text-center shadow-sm max-w-2xl mx-auto">
            <div className="text-5xl mb-4">🐾</div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">No bookings yet</h3>
            <p className="text-slate-500 mb-6">Looks like you haven't placed any bookings. Start exploring our pets and find your new best friend!</p>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteBookingId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl animate-in fade-in zoom-in duration-200">
              <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
              </div>
              <h3 className="text-xl font-bold text-center text-slate-900 mb-2">
                Cancel Booking?
              </h3>
              <p className="text-center text-slate-500 mb-8 text-sm">
                Are you sure you want to cancel this booking? This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={cancelDelete}
                  className="flex-1 px-4 py-3 bg-slate-200 text-slate-700 font-semibold text-sm rounded-xl hover:bg-amber-500 hover:text-slate-900 transition-all duration-300"
                >
                  Keep It
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-3 bg-slate-900 text-white font-semibold text-sm rounded-xl hover:bg-amber-500 hover:text-slate-900 transition-all duration-300"
                >
                  Cancel Booking
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBooking;
